import React, { useEffect, useState, useMemo } from "react";
import AppDataGrid from "../../../common/AppDataGrid";
import {
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Avatar,
  Box,
} from "@mui/material";
import {
  Edit as EditIcon,
  PieChart as PieChartIcon,
  CameraAlt as CameraIcon,
} from "@mui/icons-material";
import {
  getFirestore,
  doc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import useData from "../hooks/useData";
import EditForm from "./EditForm";
import Reports from "./Reports";
import SchoolLogo from "./SchoolLogo";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import FilterComponent from "./FilterComponent";
import { schoolsFilters } from "../../../TpPortalMgt/filterConfigurations";
import useCustomFilter from "../../../../hooks/useCustomFilter";

const CrudTable = ({ listRefresh }) => {
  const { data, loading, error, fetchData } = useData();
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [users, setUsers] = useState({}); // State to store user data by ID
  // Use custom filter hook
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
  } = useCustomFilter(data || [], schoolsFilters);

  useEffect(() => {
    fetchData();
  }, [listRefresh]);

  // Fetch users for Operations and Pathway lookup
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const db = getFirestore();
        const q = query(
          collection(db, "users"),
          where("ccpAdminFlag", "==", true),
        );
        const querySnapshot = await getDocs(q);
        const usersMap = {};
        querySnapshot.docs.forEach((doc) => {
          usersMap[doc.id] =
            doc.data().name || doc.data().displayName || "Unknown User";
        });
        setUsers(usersMap);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);

  // Create dynamic filter configuration with populated options
  const filterConfig = useMemo(() => {
    const config = [...schoolsFilters];

    // Populate district options from data
    const districtOptions = [
      ...new Set(data.map((item) => item.district).filter(Boolean)),
    ]
      .sort()
      .map((district) => ({ value: district, label: district }));

    // Populate user options for Operations and Pathway
    const userOptions = Object.entries(users)
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Update the configuration with options
    config.forEach((field) => {
      if (field.name === "district") {
        field.options = districtOptions;
      } else if (field.name === "operations" || field.name === "pathway") {
        field.options = userOptions;
      }
    });

    return config;
  }, [data, users]);

  // Filter change handler
  const handleFilterChange = (fieldName, value) => {
    setFilterModel((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    resetFilters();
  };

  // Function to get user name by ID
  const getUserName = (userId) => {
    return users[userId] || userId || "Not Assigned";
  };

  const handleEditClick = (schoolId) => {
    const school = data.find((item) => item.school_id === schoolId);
    if (school) {
      setSelectedSchool(school);
      setEditDialogOpen(true);
    }
  };

  const handleReportsClick = (schoolId) => {
    const school = data.find((item) => item.school_id === schoolId);
    if (school) {
      setSelectedSchool(school);
      setReportsDialogOpen(true);
    }
  };

  const handleLogoClick = (schoolId) => {
    const school = data.find((item) => item.school_id === schoolId);
    if (school) {
      setSelectedSchool(school);
      setLogoDialogOpen(true);
    }
  };

  const handleEditFormClose = () => {
    setEditDialogOpen(false);
    setSelectedSchool(null);
  };

  const handleReportsDialogClose = () => {
    setReportsDialogOpen(false);
    setSelectedSchool(null);
  };

  const handleLogoDialogClose = () => {
    setLogoDialogOpen(false);
    setSelectedSchool(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleUpdate = async () => {
    try {
      await fetchData(); // Refresh the list data
      setSnackbarMessage("School Info has been updated");
      setSnackbarOpen(true);
      handleLogoDialogClose();
      handleEditFormClose();
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const columns = [
    {
      field: "school_logo",
      headerName: "Logo",
      renderCell: (params) => (
        <Avatar src={params.value} alt={params.row.school_name} />
      ),
      sortable: false,
      filterable: false,
      width: 80,
    },
    { field: "school_name", headerName: "School", flex: 1 },
    { field: "district", headerName: "District", flex: 1 },
    { field: "main_contact", headerName: "Main Contact", flex: 1 },
    { field: "main_email", headerName: "Main Email", flex: 1 },
    {
      field: "operations",
      headerName: "Operations",
      flex: 1,
      renderCell: (params) => getUserName(params.value),
    },
    {
      field: "pathway",
      headerName: "Pathway",
      flex: 1,
      renderCell: (params) => getUserName(params.value),
    },
    {
      field: "second_contact",
      headerName: "Second Contact",
      flex: 1,
      hide: true,
    },
    { field: "second_email", headerName: "Second Email", flex: 1, hide: true },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={() => handleEditClick(params.row.school_id)}
            color="primary"
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => handleReportsClick(params.row.school_id)}
            color="success"
            sx={{ mr: 1 }}
          >
            <PieChartIcon />
          </IconButton>
          <IconButton
            onClick={() => handleLogoClick(params.row.school_id)}
            color="warning"
          >
            <CameraIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <>
      <FilterComponent
        filters={filterModel}
        filterConfig={filterConfig}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />
      <AppDataGrid
        rows={Array.isArray(filteredData) ? filteredData : []} // Fallback to empty array
        columns={columns}
        loading={loading}
        pagination
        pageSize={10}
        getRowId={(row) =>
          row && row.school_id ? row.school_id : Math.random()
        }
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        disableColumnFilter={true}
        initialState={{
          columns: {
            columnVisibilityModel: {
              second_contact: false,
              second_email: false,
            },
          },
        }}
      />
      <Dialog
        open={editDialogOpen}
        onClose={handleEditFormClose}
        maxWidth="lg"
        fullWidth
      >
        <ModalDialogTitle onClose={handleEditFormClose}>
          {selectedSchool
            ? `Edit ${selectedSchool.school_name}`
            : "Edit School"}
        </ModalDialogTitle>
        <DialogContent>
          {selectedSchool && (
            <EditForm
              schoolId={selectedSchool.school_id}
              onUpdate={handleUpdate}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={reportsDialogOpen}
        onClose={handleReportsDialogClose}
        maxWidth="xl"
        fullWidth
      >
        <ModalDialogTitle onClose={handleReportsDialogClose}>
          {selectedSchool ? `${selectedSchool.school_name} Reports` : "Reports"}
        </ModalDialogTitle>
        <DialogContent>
          {selectedSchool && (
            <Reports
              schoolId={selectedSchool.school_id}
              schoolName={selectedSchool.school_name}
            />
          )}
        </DialogContent>
      </Dialog>
      <SchoolLogo
        open={logoDialogOpen}
        onClose={handleLogoDialogClose}
        school={selectedSchool}
        onUpdate={handleUpdate}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </>
  );
};

export default CrudTable;
